import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Heading,
  SimpleGrid,
  useToast,
  Text,
  useColorModeValue,
  InputGroup,
  InputLeftElement,
  Input,
  Button,
  Badge,
  Card,
  CardBody,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { FiSearch, FiUser, FiMail, FiPhone, FiUsers, FiCheck, FiX } from 'react-icons/fi';
import PageLayout from '../components/PageLayout';
import {
  getLeads,
  submitLead,
  approveLead,
  rejectLead,
  assignLead,
} from '../services/api';

const LeadsManagement = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const { isOpen: isApproveDialogOpen, onOpen: onApproveDialogOpen, onClose: onApproveDialogClose } = useDisclosure();
  const { isOpen: isRejectDialogOpen, onOpen: onRejectDialogOpen, onClose: onRejectDialogClose } = useDisclosure();
  const { isOpen: isDetailsModalOpen, onOpen: onDetailsModalOpen, onClose: onDetailsModalClose } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const cardBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    fetchLeads();
  }, []);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getLeads();
      setLeads(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setError(error.message || 'Failed to fetch leads');
      setLeads([]);
      toast({
        title: 'Error',
        description: 'Failed to fetch leads',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const name = lead.clubName || '';
    const email = lead.clubEmail || '';
    const city = lead.clubCity || '';
    const country = lead.clubCountry || '';
    return (
      name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      country.toLowerCase().includes(searchQuery.toLowerCase())
    );
  });

  const stats = {
    total: leads.length,
    pending: leads.filter(lead => lead.status === 'pending').length,
    approved: leads.filter(lead => lead.status === 'approved').length,
    rejected: leads.filter(lead => lead.status === 'rejected').length,
  };

  const handleApprove = async () => {
    if (!selectedLead) return;

    try {
      const response = await approveLead(selectedLead._id, {
        status: 'approved',
        approvedAt: new Date().toISOString()
      });
      
      setLeads(prev => prev.map(l => l._id === response._id ? response : l));

      toast({
        title: 'Lead approved',
        description: 'Club has been created successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error approving lead',
        description: error.message || 'Failed to approve lead',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSelectedLead(null);
      onApproveDialogClose();
    }
  };

  const handleReject = async () => {
    if (!selectedLead) return;

    try {
      const response = await rejectLead(selectedLead._id, {
        status: 'rejected',
        rejectedAt: new Date().toISOString()
      });
      
      setLeads(prev => prev.map(lead => 
        lead._id === response._id ? response : lead
      ));

      toast({
        title: 'Lead rejected',
        description: 'Lead has been rejected successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error rejecting lead',
        description: error.message || 'Failed to reject lead',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSelectedLead(null);
      onRejectDialogClose();
    }
  };

  const handleCardClick = (lead) => {
    setSelectedLead(lead);
    onDetailsModalOpen();
  };

  const handleAssignLead = async (leadId, assigneeId) => {
    try {
      const response = await assignLead(leadId, { assigneeId });
      setLeads(prev => prev.map(lead => 
        lead._id === leadId ? response : lead
      ));
      
      toast({
        title: 'Success',
        description: 'Lead assigned successfully',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to assign lead',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const LeadCard = ({ lead }) => (
    <Card
      borderWidth="1px"
      borderRadius="lg"
      overflow="hidden"
      boxShadow="md"
      p={4}
      bg={cardBg}
      borderColor={borderColor}
      _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
      transition="all 0.2s ease-in-out"
      onClick={() => handleCardClick(lead)}
      cursor="pointer"
    >
      <CardBody p={0}>
        <VStack align="stretch" spacing={4}>
          <HStack justify="space-between">
            <Heading size="md">{lead.clubName || 'No Club Name'}</Heading>
            <Badge colorScheme={
              lead.status === 'pending' ? 'yellow' :
              lead.status === 'approved' ? 'green' : 'red'
            }>
              {lead.status}
            </Badge>
          </HStack>

          <VStack align="stretch" spacing={2}>
            <HStack>
              <FiUser />
              <Text>{lead.contactName || 'No Contact Name'}</Text>
            </HStack>
            <HStack>
              <FiMail />
              <Text>{lead.contactEmail || 'No Contact Email'}</Text>
            </HStack>
            <HStack>
              <FiPhone />
              <Text>{lead.contactPhone || 'No Contact Phone'}</Text>
            </HStack>
            <HStack>
              <FiUsers />
              <Text>{lead.clubCity || ''} {lead.clubCountry || ''}</Text>
            </HStack>
          </VStack>

          <Text fontSize="sm" color="gray.500">
            {lead.clubNeeds || 'No Description'}
          </Text>

          <SimpleGrid columns={2} spacing={4}>
            <Stat>
              <StatLabel>Services</StatLabel>
              <StatHelpText>
                {(lead.services && lead.services.length > 0) ? lead.services.join(', ') : 'N/A'}
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          {lead.status === 'pending' && (
            <HStack justify="space-between">
              <Button
                leftIcon={<FiCheck />}
                colorScheme="green"
                size="sm"
                onClick={() => {
                  setSelectedLead(lead);
                  onApproveDialogOpen();
                }}
              >
                Approve
              </Button>
              <Button
                leftIcon={<FiX />}
                colorScheme="red"
                size="sm"
                onClick={() => {
                  setSelectedLead(lead);
                  onRejectDialogOpen();
                }}
              >
                Reject
              </Button>
            </HStack>
          )}
        </VStack>
      </CardBody>
    </Card>
  );

  if (loading) {
    return (
      <Box p={4}>
        <VStack spacing={4} align="center">
          <Spinner size="xl" />
          <Text>Loading leads...</Text>
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4}>
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error loading leads</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <PageLayout>
      <Box p={4}>
        <VStack spacing={6} align="stretch">
          <Heading size="lg">Leads Management</Heading>

          {/* Stats Section */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <Card bg={cardBg}>
              <CardBody>
                <Stat>
                  <StatLabel>Total Leads</StatLabel>
                  <StatNumber>{stats.total}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg}>
              <CardBody>
                <Stat>
                  <StatLabel>Pending</StatLabel>
                  <StatNumber color="yellow.500">{stats.pending}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg}>
              <CardBody>
                <Stat>
                  <StatLabel>Approved</StatLabel>
                  <StatNumber color="green.500">{stats.approved}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
            <Card bg={cardBg}>
              <CardBody>
                <Stat>
                  <StatLabel>Rejected</StatLabel>
                  <StatNumber color="red.500">{stats.rejected}</StatNumber>
                </Stat>
              </CardBody>
            </Card>
          </SimpleGrid>

          {/* Search Section */}
          <InputGroup maxW="md">
            <InputLeftElement pointerEvents="none">
              <FiSearch color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search leads..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </InputGroup>

          {/* Leads Grid */}
          <Tabs variant="enclosed">
            <TabList>
              <Tab>All Leads</Tab>
              <Tab>Pending</Tab>
              <Tab>Approved</Tab>
              <Tab>Rejected</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredLeads.map(lead => (
                    <LeadCard key={lead._id || lead.id} lead={lead} />
                  ))}
                </SimpleGrid>
              </TabPanel>
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredLeads
                    .filter(lead => lead.status === 'pending')
                    .map(lead => (
                      <LeadCard key={lead._id || lead.id} lead={lead} />
                    ))}
                </SimpleGrid>
              </TabPanel>
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredLeads
                    .filter(lead => lead.status === 'approved')
                    .map(lead => (
                      <LeadCard key={lead._id || lead.id} lead={lead} />
                    ))}
                </SimpleGrid>
              </TabPanel>
              <TabPanel>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                  {filteredLeads
                    .filter(lead => lead.status === 'rejected')
                    .map(lead => (
                      <LeadCard key={lead._id || lead.id} lead={lead} />
                    ))}
                </SimpleGrid>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>

        {/* Approve Confirmation Dialog */}
        <AlertDialog
          isOpen={isApproveDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onApproveDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Approve Lead
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to approve this lead? This will create a new club.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onApproveDialogClose}>
                  Cancel
                </Button>
                <Button colorScheme="green" onClick={handleApprove} ml={3}>
                  Approve
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Reject Confirmation Dialog */}
        <AlertDialog
          isOpen={isRejectDialogOpen}
          leastDestructiveRef={cancelRef}
          onClose={onRejectDialogClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Reject Lead
              </AlertDialogHeader>

              <AlertDialogBody>
                Are you sure you want to reject this lead? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onRejectDialogClose}>
                  Cancel
                </Button>
                <Button colorScheme="red" onClick={handleReject} ml={3}>
                  Reject
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        {/* Lead Details Modal */}
        <Modal isOpen={isDetailsModalOpen} onClose={onDetailsModalClose} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Lead Details</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              {selectedLead && (
                <VStack align="stretch" spacing={4}>
                  <Box>
                    <Text fontWeight="bold">Requested Club:</Text>
                    <Text>{selectedLead.clubName || 'No Club Name'}</Text>
                    <Text fontSize="sm" color="gray.500">{selectedLead.clubNeeds || 'No Description'}</Text>
                  </Box>
                  <SimpleGrid columns={2} spacing={4}>
                     <Box>
                      <Text fontWeight="bold">Expected Members:</Text>
                      <Text>{selectedLead.expectedMembers || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold">Features:</Text>
                      <Text>{selectedLead.services && selectedLead.services.map(service => service.name).join(', ')}</Text>
                    </Box>
                  </SimpleGrid>
                   <Box>
                    <Text fontWeight="bold">Contact Person:</Text>
                    <Text>{selectedLead.contactName || 'No Contact Name'}</Text>
                    <Text>{selectedLead.clubEmail || 'No Contact Email'}</Text>
                    {selectedLead.contactPhone && <Text>{selectedLead.contactPhone}</Text>}
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Company:</Text>
                    <Text>{selectedLead.clubName || 'No Company'}</Text>
                  </Box>
                  <Box>
                    <Text fontWeight="bold">Status:</Text>
                    <Badge colorScheme={
                        selectedLead.status === 'pending' ? 'yellow' :
                        selectedLead.status === 'approved' ? 'green' : 'red'
                        }>
                        {selectedLead.status}
                    </Badge>
                  </Box>
                  <Box>
                      <Text fontWeight="bold">Submitted At:</Text>
                      <Text>{new Date(selectedLead.submittedAt).toLocaleString()}</Text>
                  </Box>
                   {selectedLead.notes && (
                     <Box>
                      <Text fontWeight="bold">Notes:</Text>
                       <Text>{selectedLead.notes}</Text>
                     </Box>
                   )}
                </VStack>
              )}
            </ModalBody>
          </ModalContent>
        </Modal>

      </Box>
    </PageLayout>
  );
};

export default LeadsManagement;